import { defineStore } from 'pinia'
import { ref, computed, inject } from 'vue'
import { getCurrentUser, logout as doLogout, hasRole, type UserInfo } from '@/plugins/auth'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<UserInfo | null>(null)

  // Initialize user from injected context or fetch from API
  async function init() {
    // Try to get user from injection first (set in main.ts)
    const injectedUser = inject<UserInfo | null>('currentUser', null)
    if (injectedUser) {
      user.value = injectedUser
    } else {
      // Fetch from API
      user.value = await getCurrentUser()
    }
  }

  // Set user directly (used by main.ts)
  function setUser(newUser: UserInfo | null) {
    user.value = newUser
  }

  const isAuthenticated = computed(() => user.value !== null)
  const isAdmin = computed(() => hasRole(user.value, 'admin'))
  const isSuperAdmin = computed(() => hasRole(user.value, 'superadmin'))
  const isAdminPlus = computed(() => hasRole(user.value, 'admin') || hasRole(user.value, 'superadmin'))
  const isExecutive = computed(() => hasRole(user.value, 'executive'))
  // Плановый Gantt: бэкенд пускает editor/admin/superadmin (executive и viewer — нет)
  const canViewPlanning = computed(() => hasRole(user.value, 'editor') || isAdminPlus.value)
  // Стратегический слой открывает только отдельная роль, а не иерархия admin/executive
  const hasStrategyAccess = computed(() => hasRole(user.value, 'strategy'))
  const fullName = computed(() => user.value?.name || 'Пользователь')
  const email = computed(() => user.value?.email || '')
  const roles = computed(() => user.value?.roles || [])
  const personId = computed(() => user.value?.personId || null)

  // Impersonation
  const isImpersonation = computed(() => !!user.value?.isImpersonation)
  const impersonatedBy = computed(() => user.value?.impersonatedBy || null)

  // Check if user has any app-specific role
  // Users without any role can only access settings page
  const APP_ROLES = ['executive', 'superadmin', 'admin', 'editor', 'viewer']
  const hasAppAccess = computed(() => {
    const userRoles = user.value?.roles || []
    return userRoles.some(role => APP_ROLES.includes(role))
  })

  async function signOut() {
    user.value = null
    await doLogout()
  }

  return {
    user,
    isAuthenticated,
    isAdmin,
    isSuperAdmin,
    isAdminPlus,
    isExecutive,
    canViewPlanning,
    hasStrategyAccess,
    hasAppAccess,
    fullName,
    email,
    roles,
    personId,
    isImpersonation,
    impersonatedBy,
    init,
    setUser,
    signOut,
  }
})
